import React, { useCallback, useMemo, useState } from "react";
import {
  AHEmptyScreen,
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  Circle,
  TouchableBox,
  useLayoutContext,
  useSpaceSize,
  useTheme,
} from "arkh";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { Platform, Switch } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { ProductDateTrackerStackScreenProps } from "./ProductDateTrackerStack";
import { useSizeClass } from "../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import {
  ProductDateTrackerQuery,
  ProductExpireBatchFilter,
  ProductExpireBatchFilterSortField,
} from "./__generated__/ProductDateTrackerQuery.graphql";
import { ProductDateTrackerList_Store$key } from "./__generated__/ProductDateTrackerList_Store.graphql";
import { ProductExpireBatchListItem } from "../ProductExpireBatchListItem/ProductExpireBatchListItem";
import { FormattedMessage, useIntl } from "react-intl";
import { MoreButton } from "../../common/MoreButton";
import { FlashList } from "@shopify/flash-list";
import { Image } from "expo-image";
import { useBarcodeScanner } from "../../common/useBarcodeScanner";

function ProductDateTrackerList(props: {
  store: ProductDateTrackerList_Store$key;
  onPressBatch: (batchId: string) => void;
  onRequestCreate?: () => void;
  filter: ProductExpireBatchFilter | null;
  bottomSafeArea: boolean;
}) {
  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();

  const { data, loadNext, hasNext } = usePaginationFragment(
    graphql`
      fragment ProductDateTrackerList_Store on Store
      @refetchable(queryName: "ProductDateTrackerListPaginationQuery") {
        productExpireBatches(
          after: $after
          first: $first
          filter: $filter
          orderBy: $order
        ) @connection(key: "ProductDateTracker__productExpireBatches") {
          edges {
            cursor
            node {
              id
              activeAction {
                startAtDate {
                  isoString
                  timezone
                }
              }

              ...ProductExpireBatchListItem_StoreProductExpireBatch
              expireAtDate {
                isoString
                timezone
                ...StoreRelativeZonedDate_StoreZonedDate
              }
              status
              product {
                id
                name
                imageUrl
                partNumber
                ...ProductHeader_ProductDetails
                ...ProductExpireBatchProductPreview_StoreProduct
              }
            }
          }
        }
      }
    `,
    props.store
  );

  const listData = useMemo(() => {
    return data.productExpireBatches?.edges ?? [];
  }, [data.productExpireBatches?.edges]);

  const renderItem = ({
    item,
  }: {
    item: (typeof listData)[number];
    index: number;
  }) => {
    return (
      <ProductExpireBatchListItem
        onPress={props.onPressBatch}
        productExpireBatch={item.node}
      />
    );
  };

  const space = useSpaceSize("medium");

  if (listData.length === 0) {
    return (
      <>
        {props.filter === "ONLY_ACTIONS" ? (
          <AHEmptyScreen
            title={
              <FormattedMessage
                defaultMessage="You’re caught up"
                description="Product Date Tracker Nothing To Handle Empty Screen title"
              />
            }
            subtitle={
              <FormattedMessage
                defaultMessage="Everything is on schedule, no actions needed."
                description="Product Date Tracker Nothing To Handle Empty Screen subtitle"
              />
            }
            illustration={
              <Image
                contentFit="contain"
                style={{
                  flex: 1,
                }}
                source={require("./NothingToHandle.png")}
              />
            }
          />
        ) : (
          <AHEmptyScreen
            title={
              <FormattedMessage
                defaultMessage="Add Dates"
                description="Product Date Tracker Empty Screen title"
              />
            }
            subtitle={
              <FormattedMessage
                defaultMessage="At the moment, there are no products with an expiration date."
                description="Product Date Tracker Empty Screen subtitle"
              />
            }
            illustration={
              <Image
                contentFit="contain"
                style={{
                  flex: 1,
                }}
                source={require("./NoActiveExpireDate.png")}
              />
            }
          />
        )}
        {props.onRequestCreate ? (
          <TouchableBox
            onPress={props.onRequestCreate}
            style={{
              position: "absolute",
              bottom:
                layout.space.medium +
                (props.bottomSafeArea ? insets.bottom : 0),
              right: layout.space.medium,
            }}
          >
            <Circle size="large" color="primary">
              <AHIcon type="add" color="text-inverted" forceSize={20} />
            </Circle>
          </TouchableBox>
        ) : null}
      </>
    );
  }

  return (
    <>
      <FlashList
        onEndReached={() => {
          if (hasNext) {
            loadNext(30);
          }
        }}
        keyExtractor={(item) => item.node.id}
        onEndReachedThreshold={0.5}
        keyboardShouldPersistTaps="always"
        ItemSeparatorComponent={AHSeparator}
        contentContainerStyle={{
          paddingBottom: insets.bottom + space,
        }}
        scrollIndicatorInsets={{ bottom: insets.bottom + layout.space.large }}
        data={listData}
        renderItem={renderItem}
      />
      {props.onRequestCreate ? (
        <TouchableBox
          onPress={props.onRequestCreate}
          style={{
            position: "absolute",
            bottom:
              layout.space.medium + (props.bottomSafeArea ? insets.bottom : 0),
            right: layout.space.medium,
          }}
        >
          <Circle size="large" color="primary">
            <AHIcon type="add" color="text-inverted" forceSize={20} />
          </Circle>
        </TouchableBox>
      ) : null}
    </>
  );
}

function ProductDateTrackerComponent(
  props: ProductDateTrackerStackScreenProps<"ProductDateTrackerMain"> & {
    fetchKey: string;
    filter: ProductExpireBatchFilter | null;
    order: ProductExpireBatchFilterSortField;
  }
) {
  const data = useLazyLoadQuery<ProductDateTrackerQuery>(
    graphql`
      query ProductDateTrackerQuery(
        $first: Int
        $after: String
        $filter: ProductExpireBatchFilter
        $order: ProductExpireBatchFilterSortField!
      ) {
        currentStore @required(action: THROW) {
          ...ProductDateTrackerList_Store
        }
      }
    `,
    {
      first: 50,
      after: null,
      filter: props.filter,
      order: props.order,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  const onPressBatch = useCallback(
    (id: string) => {
      props.navigation.navigateDeprecated("ProductExpireBatch", {
        id,
      });
    },
    [props.navigation]
  );
  return (
    <ProductDateTrackerList
      filter={props.filter}
      bottomSafeArea={isCompact}
      store={data.currentStore}
      onPressBatch={onPressBatch}
      onRequestCreate={() => {
        props.navigation.navigateDeprecated("ProductExpireBatchCreate", {});
      }}
    />
  );
}

export function ProductDateTracker(
  props: ProductDateTrackerStackScreenProps<"ProductDateTrackerMain">
) {
  const [filter, setFilter] = useState<ProductExpireBatchFilter | null>(null);

  const [orderField, setOrderField] =
    useState<ProductExpireBatchFilterSortField>("EXPIRE_AT");

  const theme = useTheme();
  const colorProps =
    Platform.OS === "ios"
      ? {
          trackColor: {
            true: theme.primary,
            false: theme["border-strong"],
          },
        }
      : ({
          activeThumbColor: theme.primary,
          activeTrackColor: theme["track-background"],

          thumbColor: theme["surface-primary"],
          trackColor: {
            true: theme["track-background"],
            false: theme["border-strong"],
          },
        } as any);

  const intl = useIntl();
  const expireAtLabel = intl.formatMessage({
    defaultMessage: "Expire date",
    description: "ProductDateTracker Expire at label",
  });
  const createdAtLabel = intl.formatMessage({
    defaultMessage: "Created at",
    description: "ProductDateTracker Created at label",
  });

  useBarcodeScanner(
    useCallback(
      (ean) => {
        props.navigation.navigateDeprecated("Product", {
          screen: "ProductMain",
          params: {
            ean,
            type: "ean",
            state: "expireDates-add",
          },
        });
      },
      [props.navigation]
    )
  );

  return (
    <>
      <Box
        color="surface-primary"
        spaceHorizontal="small"
        spaceVertical={Platform.OS === "android" ? "none" : "small"}
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottomWidth: 1,
        }}
        borderColor="border-primary"
      >
        <MoreButton
          items={[
            {
              label: expireAtLabel,
              icon: "flag",
              type: orderField === "EXPIRE_AT" ? "selected-check" : "button",
              onPress: () => {
                setOrderField("EXPIRE_AT");
              },
            },
            {
              label: createdAtLabel,
              icon: "add",
              type: orderField === "CREATED_AT" ? "selected-check" : "button",
              onPress: () => {
                setOrderField("CREATED_AT");
              },
            },
          ]}
        >
          {(op) => (
            <TouchableBox
              onPress={op}
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}
              spaceHorizontal="small"
              borderColor="border-primary"
            >
              <AHText type="subtitle" color="text-secondary" spaceRight="small">
                <FormattedMessage
                  description="ProductDateTracker sort label"
                  defaultMessage="Sort: "
                />{" "}
                {orderField === "CREATED_AT" ? createdAtLabel : expireAtLabel}
              </AHText>
              <AHIcon type="chevron-down" color="text-secondary" />
            </TouchableBox>
          )}
        </MoreButton>

        <TouchableBox
          spaceHorizontal="small"
          style={{
            flexDirection: "row",
            alignItems: "center",
          }}
          onPress={() => {
            setFilter((f) => (f === "ONLY_ACTIONS" ? null : "ONLY_ACTIONS"));
          }}
        >
          <AHText type="subtitle" color="text-secondary" spaceRight="small">
            <FormattedMessage
              description="ProductDateTracker filter label"
              defaultMessage="With actions"
            />
          </AHText>
          <Switch
            {...colorProps}
            active
            hitSlop={{
              left: 100,
            }}
            value={filter === "ONLY_ACTIONS"}
            onValueChange={(val) => {
              if (val) {
                setFilter("ONLY_ACTIONS");
              } else {
                setFilter(null);
              }
            }}
          />
        </TouchableBox>
      </Box>
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey) => (
          <ProductDateTrackerComponent
            {...props}
            fetchKey={fetchKey}
            filter={filter}
            order={orderField}
          />
        )}
      </LazyQueryNetworkBoundrary>
    </>
  );
}
